<script setup>
import { ref, watch, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import SectionLayout from './SectionLayout.vue';
import WithLabel from 'v3/components/Form/WithLabel.vue';
import TextArea from 'next/textarea/TextArea.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import SingleSelect from 'dashboard/components-next/filter/inputs/SingleSelect.vue';

const { t } = useI18n();
const store = useStore();
const messageTemplate = ref('');
const selectedInbox = ref(null);
const isSubmitting = ref(false);

const { currentAccount } = useAccount();

const inboxes = useMapGetter('inboxes/getInboxes');

const availableInboxOptions = computed(() => {
  if (!inboxes.value?.length) return [];
  return inboxes.value
    .filter(inbox =>
      inbox.channel_type === 'Channel::Whatsapp' ||
      inbox.channel_type === 'Channel::Api' ||
      inbox.channel_type === 'Channel::TwilioSms'
    )
    .map(inbox => ({
      id: inbox.id,
      name: inbox.name,
    }));
});

// Fetch inboxes on mount
onMounted(async () => {
  await store.dispatch('inboxes/get');
});

// eslint-disable-next-line no-useless-concat
const defaultTemplate = '*Nova Entrega - Pedido ' + '{{pedido_id}}' + '*\n\n' +
  'Olá ' + '{{nome_entregador}}' + '!\n\n' +
  'Você tem uma nova entrega:\n\n' +
  '*Cliente:* ' + '{{cliente}}' + '\n' +
  '*Telefone:* ' + '{{telefone_cliente}}' + '\n' +
  '*Endereço:* ' + '{{endereco}}' + '\n\n' +
  '*Itens:*\n' + '{{itens}}' + '\n\n' +
  '*Total:* ' + '{{total}}' + '\n\n' +
  'Por favor, confirme o recebimento desta mensagem.';

watch(
  [currentAccount, availableInboxOptions],
  () => {
    const customAttrs = currentAccount.value?.custom_attributes || {};

    messageTemplate.value = customAttrs.delivery_notification_template || '';

    const inboxId = customAttrs.delivery_notification_inbox_id;
    if (inboxId && availableInboxOptions.value.length) {
      selectedInbox.value = availableInboxOptions.value.find(
        inbox => inbox.id === inboxId
      );
    }
  },
  { deep: true, immediate: true }
);

const handleSubmit = async () => {
  try {
    isSubmitting.value = true;
    // Use the store action to update the account
    await store.dispatch('accounts/update', {
      custom_attributes: {
        ...currentAccount.value?.custom_attributes,
        delivery_notification_template: messageTemplate.value,
        delivery_notification_inbox_id: selectedInbox.value?.id || null,
      },
    });
    useAlert('Configurações de entrega salvas com sucesso!');
  } catch (error) {
    console.error('Error saving delivery settings:', error);
    useAlert('Erro ao salvar configurações de entrega');
  } finally {
    isSubmitting.value = false;
  }
};

const useDefaultTemplate = () => {
  messageTemplate.value = defaultTemplate;
};
</script>

<template>
  <SectionLayout
    title="Configurações de Entrega"
    description="Configure a mensagem padrão enviada aos entregadores quando um pedido é separado para entrega."
    with-border
  >
    <form class="grid gap-5" @submit.prevent="handleSubmit">
      <WithLabel
        label="Caixa de Entrada para Notificações"
        help-message="Selecione a caixa de entrada que será usada para enviar notificações aos entregadores."
      >
        <SingleSelect
          v-model="selectedInbox"
          :options="availableInboxOptions"
          placeholder="Selecione uma caixa de entrada"
          placeholder-icon="i-lucide-chevron-down"
          placeholder-trailing-icon
          class="w-full"
        />
        <p v-if="availableInboxOptions.length === 0" class="text-xs text-n-slate-11 mt-1">
          Nenhuma caixa de entrada disponível.
        </p>
      </WithLabel>

      <WithLabel
        label="Mensagem Padrão para Entregadores"
        help-message="Esta mensagem será enviada aos entregadores quando um pedido for separado para entrega."
      >
        <TextArea
          v-model="messageTemplate"
          class="w-full"
          rows="10"
          placeholder="Digite a mensagem padrão..."
        />
        <div class="mt-2 text-xs text-n-slate-11">
          <p class="font-medium mb-1">Variáveis disponíveis:</p>
          <div class="flex flex-wrap gap-2">
            <code class="bg-n-alpha-2 px-1.5 py-0.5 rounded" v-pre>{{nome_entregador}}</code>
            <code class="bg-n-alpha-2 px-1.5 py-0.5 rounded" v-pre>{{pedido_id}}</code>
            <code class="bg-n-alpha-2 px-1.5 py-0.5 rounded" v-pre>{{cliente}}</code>
            <code class="bg-n-alpha-2 px-1.5 py-0.5 rounded" v-pre>{{telefone_cliente}}</code>
            <code class="bg-n-alpha-2 px-1.5 py-0.5 rounded" v-pre>{{endereco}}</code>
            <code class="bg-n-alpha-2 px-1.5 py-0.5 rounded" v-pre>{{total}}</code>
            <code class="bg-n-alpha-2 px-1.5 py-0.5 rounded" v-pre>{{itens}}</code>
          </div>
        </div>
        <NextButton
          type="button"
          faded
          slate
          sm
          class="mt-2"
          label="Usar modelo padrão"
          icon="i-lucide-file-text"
          @click="useDefaultTemplate"
        />
      </WithLabel>

      <div class="flex gap-2">
        <NextButton
          blue
          type="submit"
          :is-loading="isSubmitting"
          label="Salvar Configurações"
        />
      </div>
    </form>
  </SectionLayout>
</template>
